<script setup lang="ts">
import { copyToClipboard } from '@/utils/apostatudo/clipboard';

const props = defineProps<{
  deposit: DefaultDepositInfoResponse;
}>();

type Emits = {
  toggleModalInner: [];
  'countdown-done': [];
};

const emit = defineEmits<Emits>();

const { isMobile } = useLayout();

function closePaymentInfos() {
  emit('toggleModalInner');
}

const inputCodeElement = ref();

const isBrCodeCopied = ref<boolean>(false);

const copyCode = async () => {
  const codeToCopy = props.deposit.br_code || 'Fail';

  try {
    inputCodeElement.value?.select?.();
  } catch {}

  if (await copyToClipboard(codeToCopy)) {
    isBrCodeCopied.value = true;
    setTimeout(() => (isBrCodeCopied.value = false), 2000);
  }
};

const show_qr_code = ref(false);

const toggleShowQrCode = () => {
  show_qr_code.value = !show_qr_code.value;
};

const timeToExpirePayment = ref<number>(300);

function onTimerEnded() {
  emit('countdown-done');
}

onUnmounted(() => {
  if (localStorage.getItem('start_countdown')) {
    localStorage.removeItem('start_countdown');
  }

  if (localStorage.getItem('init_countdown')) {
    localStorage.removeItem('init_countdown');
  }
});
</script>

<template>
  <div
    ref="deposit-payment"
    class="deposit-payment"
  >
    <div
      class="flex flex-row items-center gap-3 cursor-pointer mb-4"
      @click="closePaymentInfos()"
    >
      <NuxtIcon
        class="text-auth-texts/80"
        name="arrows/arrow_left"
      />
      <div class="flex flex-col">
        <span class="text-auth-texts font-semibold text-base text-left">
          {{ $t('deposit.general.go_back') }}
        </span>
      </div>
    </div>

    <div class="text-center mt-3 mb-5">
      <h5
        v-if="!isMobile"
        class="scan-title"
      >
        {{ $t('account.wallet.scan_image') }}
        <br />
        {{ $t('account.wallet.to_make_payment') }}
      </h5>

      <h5
        v-else
        class="scan-title"
      >
        {{ $t('account.wallet.copy_code') }}
      </h5>

      <div
        v-if="!isMobile"
        class="depositbr_code"
      >
        <img
          id="qr-code-img"
          alt="Codigo QR PIX"
          fluid
          :src="deposit.qr_code"
        />
      </div>

      <DepositAlertCpf />

      <div
        class="clickToCopy"
        @click="copyCode"
      >
        <div class="handIcon">
          <NuxtIcon name="hand_pointer" />
        </div>
        <div class="my-3">
          <p
            ref="input"
            class="depositValue"
          >
            {{ deposit.amount }}
          </p>
        </div>

        <div class="inputCode">
          <input
            ref="inputCodeElement"
            class="input-center small"
            readonly
            type="text"
            :value="props.deposit.br_code"
          />
        </div>

        <div>
          <button
            :class="['buttonsBottom', appNameLower()]"
            size="lg"
            variant="secondary"
          >
            <NuxtIcon
              v-if="isBrCodeCopied"
              name="check"
            />
            {{
              isBrCodeCopied
                ? $t('account.wallet.code_copied')
                : $t('account.wallet.copy_code_button')
            }}
          </button>
        </div>
      </div>

      <div class="timer mt-5 mb-0">
        <WidgetsCountdown
          class="text-xs"
          :progress-bar="true"
          :total-time="timeToExpirePayment"
          @timer-ended="onTimerEnded"
        >
          <template #process="{ time }">
            <div class="timer_wrapper flex flex-col text-left">
              <span
                class="bonus-countdown-expire text-warning rounded-l-md font-bold"
              >
                {{ $t('deposit.pix_timer') }}
              </span>
              <span
                class="text-xl bonus-countdown-time text-texts rounded-r-md font-bold"
              >
                {{ timeToExpirePayment >= 3600 ? `${time.hours}:` : ''
                }}{{ `${time.minutes}:${time.seconds}` }}
              </span>
            </div>
          </template>
          <template #finish>
            <span
              class="bonus-countdown-expire text-error text-xl rounded-l-md font-bold"
            >
              {{ $t('deposit.pix_timer_finished') }}</span
            >
          </template>
        </WidgetsCountdown>
      </div>

      <div class="mt-3">
        <button
          class="buttonsBottom small"
          variant="primary"
        >
          <BaseLoading
            :full-screen="false"
            inline
            show
            size="xsmall"
          />

          {{ $t('account.wallet.awaiting_payment') }}
        </button>
      </div>

      <div
        v-if="isMobile && show_qr_code"
        class="depositbr_code"
      >
        <img
          id="qr-code-img"
          alt="Codigo QR PIX"
          fluid
          :src="deposit.qr_code"
        />
      </div>
      <button
        v-if="isMobile"
        class="text-auth-texts/70 text-xs font-medium"
        :class="show_qr_code ? 'mt-4' : 'mt-6'"
        type="button"
        @click="toggleShowQrCode"
      >
        {{
          !show_qr_code
            ? $t('account.wallet.show_qr_code')
            : $t('account.wallet.hide_qr_code')
        }}
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.deposit-payment {
  .scan-title {
    @apply text-auth-texts m-1 mb-2 max-w-[280px] mx-auto;
  }
  .clickToCopy {
    @apply border-dashed border-auth-texts/60 border rounded-lg px-7 py-8 my-5 relative mx-1 md:mx-4;
    .handIcon {
      @apply absolute top-4 right-4 text-auth-texts/40 text-3xl cursor-pointer;
    }
    .depositValue {
      @apply text-primary font-bold text-3xl leading-3 -mt-1 mb-5;
    }
    .inputCode {
      @apply mb-2 md:mb-3 w-full;
      .input-center {
        @apply bg-auth-bg-inputs text-auth-text-inputs/80 text-center rounded-lg w-full px-4 h-[50px] text-xs;
      }
    }
  }
  .timer {
    @apply px-3 pb-4 my-5 relative mx-1 md:mx-4;
  }
  .buttonsBottom {
    @apply inline-flex items-center justify-center gap-2;
    @apply text-auth-button-bg bg-auth-button-bg/10;
    @apply py-2 md:py-3 px-6 rounded w-full md:w-auto;
    &.small {
      @apply text-sm py-2 px-5 mt-2 -mb-2 text-success bg-success/10 w-auto;
    }
  }
  .depositbr_code {
    @apply h-fit w-fit p-2 mx-auto bg-white border-primary border-[5px] rounded-lg overflow-hidden mt-5;
    img {
      @apply max-w-[220px];
    }
  }
}
</style>
